// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { CircularProgress, Tooltip } from '@mui/material';
import { AppIcon, formatDate, Translation } from '@shared/ui-kit';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';

export type RefreshControlMode = 'sync' | 'update';

export interface RefreshControlProps {
  updatedDate?: string | null | undefined;
  onRefresh?: () => void;
  disabled?: boolean;
  loading?: boolean;
  /**
   * `sync` — provider sync copy (bank accounts). `update` — refresh/reload copy
   * (e.g. Nuvara Inversis, manual institutions, illiquid assets).
   */
  mode?: RefreshControlMode;
}

export const RefreshControl: FC<RefreshControlProps> = ({
  updatedDate,
  onRefresh,
  disabled,
  loading,
  mode = 'sync',
}) => {
  const { i18n, t } = useTranslation();

  const tooltipTitle = t(
    mode === 'update'
      ? 'glossary.update-data-tooltip'
      : 'glossary.sync-data-tooltip',
    { ns: 'glossary' },
  );

  return (
    <div className="flex items-center gap-x-5" data-testid="refresh-control">
      {loading ? (
        <div
          className="flex items-center gap-x-2 text-lg font-medium leading-8 text-primary"
          data-testid="syncing-container"
        >
          <CircularProgress
            size={18}
            color="primary"
            data-testid="loading-spinner"
          />
          {mode === 'update' ? (
            <Translation i18nKey="glossary.updating-data" ns="glossary" />
          ) : (
            <Translation i18nKey="glossary.syncing-data" ns="glossary" />
          )}
        </div>
      ) : (
        <Tooltip title={tooltipTitle}>
          <span>
            <button
              onClick={onRefresh}
              disabled={disabled}
              aria-label={tooltipTitle}
              className="flex items-center gap-x-2 text-lg font-medium leading-8 text-primary transition-opacity hover:opacity-70 disabled:cursor-not-allowed disabled:text-gray-400"
              data-testid={
                updatedDate || mode === 'update'
                  ? 'refresh-button'
                  : 'sync-all-button'
              }
            >
              <AppIcon
                icon="RefreshTwoArrows"
                size="lg"
                color={disabled ? 'disabled' : 'inherit'}
              />
              {mode === 'update' ? (
                <Translation
                  i18nKey="glossary.update-data-available"
                  ns="glossary"
                />
              ) : updatedDate ? (
                <Translation
                  i18nKey="glossary.synced-on"
                  ns="glossary"
                  values={{
                    date: formatDate(updatedDate, 'long', i18n.language),
                  }}
                />
              ) : (
                <Translation i18nKey="glossary.sync-data-available" ns="glossary" />
              )}
            </button>
          </span>
        </Tooltip>
      )}
    </div>
  );
};
